import { useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import type { FieldValues, UseFormSetValue } from "react-hook-form";
import type { AdmissionOfficeCountry } from "@/src/utils/admissionOffice/resolveAdmissionOfficeRouteFromTask";
import { applyAdmissionTaskPrefillToForm } from "@/src/utils/admissionOffice/applyAdmissionTaskPrefillToForm";

export { normalizeDateForInput } from "@/src/constants/AdmissionOffice/admissionTaskPrefillMappings";

/**
 * Shared payload contract used by the Tasks → Admission Office redirect flow.
 * Keep keys here in sync with `buildAdmissionPrefillPayloadFromTask`.
 */
export interface AdmissionTaskPrefillPayload {
  source?: string;
  rowId?: number;
  task_id?: string;
  student_id?: number | string | null;
  applicant_id?: number | string | null;
  fullName?: string;
  fullNameNat?: string;
  dateOfBirth?: string;
  nationality?: string;
  formOfEducation?: string;
  admissionSemester?: string;
  intake?: string;
  category?: string;
  course?: string;
  degree?: string;
  selectedCourse?: string;
  mediumOfInstruction?: string;
  duration?: string;
  whoose?: string;
  tuitionFee?: string;
  transfer?: string;
  medicalInsurance?: string;
  residencePermit?: string;
  otherExpenses?: string;
  acommodation?: string;
  admissionFee?: string;
  completeOneYear?: string;
  priority?: string;
  note?: string;
  deadlineDate?: string;
  form_office?: string;
  office?: string;
  type?: string;
  targetCountry?: AdmissionOfficeCountry;
  targetDocument?: string;
  targetLang?: string;
  [key: string]: unknown;
}

export interface AdmissionTaskPrefillMapping {
  formField: string;
  payloadKey: keyof AdmissionTaskPrefillPayload;
  transform?: (value: unknown) => string;
}

/** Form field values captured right after task prefill (baseline for approve diff). */
export type AdmissionTaskPrefillFormBaseline = Record<string, string>;

interface UseAdmissionTaskPrefillOptions<TForm extends FieldValues> {
  setValue: UseFormSetValue<TForm>;
  country: AdmissionOfficeCountry;
  /** Router document slug; falls back to `:document` route param. */
  documentSlug?: string;
  /**
   * Field mappings. Defaults to `standardCourseDocumentTaskPrefillMapping`.
   * Pass `admissionLetterTaskPrefillMapping` (or custom) for admission letter forms.
   */
  mapping?: AdmissionTaskPrefillMapping[];
  onPrefill?: (
    payload: AdmissionTaskPrefillPayload,
    mapping: AdmissionTaskPrefillMapping[],
    formBaseline: AdmissionTaskPrefillFormBaseline,
  ) => void;
}

export const STORAGE_KEY = "admissionOfficePrefillFromTask";

const readPayloadForTarget = (
  targetCountry: string,
  targetDocument: string,
): AdmissionTaskPrefillPayload | null => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;

  let payload: AdmissionTaskPrefillPayload | null = null;
  try {
    payload = JSON.parse(raw) as AdmissionTaskPrefillPayload;
  } catch (error) {
    console.warn("[AdmissionOffice Prefill] Failed to parse payload:", error);
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }

  if (!payload || payload.source !== "tasks") {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }

  const payloadCountry = payload.targetCountry;
  const payloadDocument = payload.targetDocument;

  if (payloadCountry && payloadDocument) {
    if (payloadCountry !== targetCountry || payloadDocument !== targetDocument) {
      return null;
    }
  }

  localStorage.removeItem(STORAGE_KEY);
  return payload;
};

/**
 * Applies Tasks → Admission Office prefill only on the target document page
 * (`targetCountry` / `targetDocument` in localStorage). Payload is not consumed
 * until the matching route mounts.
 */
export const useAdmissionTaskPrefill = <TForm extends FieldValues>({
  setValue,
  country,
  documentSlug,
  mapping,
  onPrefill,
}: UseAdmissionTaskPrefillOptions<TForm>) => {
  const { document: documentFromRoute } = useParams<{ document?: string }>();
  const resolvedDocument = documentSlug ?? documentFromRoute ?? "";
  const lastPrefilledTaskIdRef = useRef("");

  const applyTaskPrefill = useCallback(
    (payload: AdmissionTaskPrefillPayload) => {
      const { finalMapping, formBaseline, usedMappings, skippedMappings } = applyAdmissionTaskPrefillToForm({
        payload,
        setValue,
        mapping,
      });

      if (payload.task_id) {
        lastPrefilledTaskIdRef.current = String(payload.task_id).trim();
      }

      const knownKeys = new Set<string>([
        ...finalMapping.map((item) => String(item.payloadKey)),
        "source",
        "targetCountry",
        "targetDocument",
        "targetLang",
        "form_office",
        "office",
        "type",
        "applicant_id",
        "unmappedTaskFields",
      ]);
      const unusedPayloadKeys = Object.keys(payload).filter((key) => !knownKeys.has(key));

      console.groupCollapsed(`[AdmissionOffice Prefill] ${country}/${resolvedDocument}`);
      console.log("Used mappings:", usedMappings);
      console.log("Skipped mappings:", skippedMappings);
      console.log("Unused payload keys:", unusedPayloadKeys);
      console.log("Raw payload:", payload);
      console.groupEnd();

      onPrefill?.(payload, finalMapping, formBaseline);
    },
    [country, mapping, onPrefill, resolvedDocument, setValue],
  );

  useEffect(() => {
    if (!resolvedDocument) return;

    const payload = readPayloadForTarget(country, resolvedDocument);
    if (!payload) return;

    applyTaskPrefill(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, resolvedDocument]);

  return { applyTaskPrefill, lastPrefilledTaskIdRef };
};
